import cx from 'classnames';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { BreakPointName, useBreakPoint } from '../../../domains/Layout/BreakPoint/BreakPoint';
import { IconButton } from '../form/button/IconButton';
import { IconArrowLeft } from '../icons/ArrowLeft';
import { IconArrowRight } from '../icons/ArrowRight';

import style from './Carousel.module.css';

export interface CarouselState {
    currentIndex: number;
    setCurrentIndex: (index: React.SetStateAction<number>) => void;
}

export type CarouselProps = PropsWithChildren<
    CarouselState & {
        className?: string;
        fullHeight?: boolean;
        navButtonsPosition?: 'inside' | 'overlap';
        previousLabel: string;
        withDots?: boolean;
        nextLabel: string;
        slidesSize?: 'medium' | 'large' | 'full';
        slidesVisibleRules: Omit<
            {
                [K in BreakPointName]: number;
            },
            'xs'
        >;
        onPrevClick?: () => void;
        onNextClick?: () => void;
    }
>;

export const CAROUSEL_TEST_ID = 'Carousel';

export function useCarousel(initialIndex = 0) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    return {
        currentIndex,
        setCurrentIndex,
    };
}

export function Carousel({
    children,
    className,
    currentIndex,
    fullHeight = false,
    navButtonsPosition = 'overlap',
    previousLabel,
    nextLabel,
    withDots = false,
    setCurrentIndex,
    slidesSize = 'medium',
    slidesVisibleRules,
    onPrevClick,
    onNextClick,
}: CarouselProps) {
    const { currentBreakpoint } = useBreakPoint();

    const translateContainerRef = useRef<HTMLDivElement>(null);

    const slidesCount = React.Children.count(children);
    const nbSlidesPerPage = currentBreakpoint === 'xs' ? 1 : slidesVisibleRules[currentBreakpoint];
    const overflowingIndex =
        slidesCount < nbSlidesPerPage ? 0 : (slidesCount % nbSlidesPerPage) / nbSlidesPerPage;
    const indexMax =
        slidesCount < nbSlidesPerPage
            ? 0
            : Math.floor(slidesCount / nbSlidesPerPage) - 1 + overflowingIndex;
    const nbDots = Math.ceil(indexMax) + 1;

    const isPreviousButtonHidden = currentIndex === 0;
    const isNextButtonHidden = Math.abs(currentIndex) === indexMax;

    const slideClassName = Object.entries(slidesVisibleRules)
        .map(([key, count]) => {
            return `col-${key}-${12 / count}`;
        })
        .join(' ');

    useEffect(() => {
        const scrollElement = translateContainerRef.current;

        if (scrollElement === null) {
            return;
        }

        const handleScroll = () => {
            const { clientWidth, scrollLeft, scrollWidth } = scrollElement;
            const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;
            const slidesPercentage = 1 / slidesCount;
            const newIndex = Math.round(scrollPercentage / slidesPercentage) - 1;

            if (newIndex === currentIndex) {
                return;
            }
            setCurrentIndex(newIndex);
        };

        scrollElement.addEventListener('scroll', handleScroll);

        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
        };
    }, [slidesCount, currentIndex, setCurrentIndex]);

    /* When currentBreakpoint changes for a wider one (windows resized)
       the currentIndex might be greater than the maxIndex */
    useEffect(() => {
        if (currentIndex > indexMax) {
            setCurrentIndex(indexMax);
        }
    }, [currentBreakpoint, currentIndex, indexMax, setCurrentIndex]);

    return (
        <div
            className={cx(className, style.carousel, {
                [style.carouselFullHeight]: fullHeight,
            })}
            data-current={currentIndex}
            data-testid={CAROUSEL_TEST_ID}
        >
            <IconButton
                className={cx('hidden-xs', style.navButton, {
                    hidden: isPreviousButtonHidden,
                    [style.navLeftButtonOverlap]: navButtonsPosition === 'overlap',
                    [style.navLeftButtonInside]: navButtonsPosition === 'inside',
                })}
                Icon={IconArrowLeft}
                onClick={() => {
                    setCurrentIndex((prevTranslationIndex) => {
                        if (prevTranslationIndex === indexMax && overflowingIndex > 0) {
                            return Math.floor(prevTranslationIndex);
                        }
                        return prevTranslationIndex - 1;
                    });

                    if (typeof onPrevClick === 'function') {
                        onPrevClick();
                    }
                }}
                data-testid="CarouselPreviousButton"
                aria-hidden={isNextButtonHidden}
                aria-label={previousLabel}
                type="button"
            />

            <div className={style.overflowContainer}>
                <div
                    ref={translateContainerRef}
                    className={cx('row', style.translateContainer)}
                    data-testid="CarouselTranslateContainer"
                    // eslint-disable-next-line react/forbid-dom-props
                    style={{
                        transform: `translateX(-${currentIndex * 100}%)`,
                    }}
                >
                    {React.Children.map(children, (child, index) => {
                        if (!React.isValidElement(child)) {
                            return null;
                        }
                        return (
                            <div
                                key={index}
                                className={cx(slideClassName, style.slide, {
                                    [style.slideMedium]: slidesSize === 'medium',
                                    [style.slideLarge]: slidesSize === 'large',
                                    [style.slideFull]: slidesSize === 'full',
                                })}
                            >
                                {child}
                            </div>
                        );
                    })}
                </div>
            </div>

            <IconButton
                className={cx('hidden-xs', style.navButton, {
                    hidden: isNextButtonHidden,
                    [style.navRightButtonOverlap]: navButtonsPosition === 'overlap',
                    [style.navRightButtonInside]: navButtonsPosition === 'inside',
                })}
                Icon={IconArrowRight}
                onClick={() => {
                    setCurrentIndex((prevTranslationIndex) => {
                        if (prevTranslationIndex === indexMax - overflowingIndex) {
                            return indexMax;
                        }
                        return prevTranslationIndex + 1;
                    });

                    if (typeof onNextClick === 'function') {
                        onNextClick();
                    }
                }}
                data-testid="CarouselNextButton"
                aria-hidden={isNextButtonHidden}
                aria-label={nextLabel}
                type="button"
            />

            {withDots && nbDots > 1 ? (
                <div className={style.dots}>
                    {Array.from(Array(nbDots)).map((_, index) => (
                        <div
                            key={`dots ${index}`}
                            className={cx(style.dot, {
                                [style.selectedDot]: index === Math.ceil(currentIndex),
                            })}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
